import React, { useCallback, useMemo } from 'react';

import { IntegrationDomain, IntegrationVersion } from 'api/usePublisherIntegrationTest';

import { IntegrationTestModal } from 'components/IntegrationTestModal/IntegrationTestModal';

import { docsUrls } from '../DeveloperSettings.constants';
import { IIntegrationSectionDependencies } from '../DeveloperSettings.integration';
import { EApiVersion, ESavingForm } from '../DeveloperSettings.types';
import { IVersionConfig } from '../DeveloperSettings.types';
import { useApiTestingAnalytics } from '../hooks/useApiTestingAnalytics';
import { useEventsWebhookForm } from '../hooks/useCallbackForms';
import { useIntegrationTestModal } from '../hooks/useIntegrationTestModal';

import { CallbackSection } from './CallbackSection';

type EventsWebhookSectionBaseDependencies = Pick<
  IIntegrationSectionDependencies,
  'publisherId' | 'integrationData' | 'updateIntegration' | 'getIntegration' | 'savingForm' | 'setSavingForm' | 'isProfilesEnabled'
>;

interface IEventsWebhookSectionProps extends EventsWebhookSectionBaseDependencies {
  isEventsV2FeatureFlagOn: boolean;
}

const resolveEventsWebhookVersion = (isEventsV2FeatureFlagOn: boolean): IntegrationVersion => {
  if (isEventsV2FeatureFlagOn) {
    return IntegrationVersion.V2;
  }
  return IntegrationVersion.V1;
};

export const EventsWebhookSection: React.FC<IEventsWebhookSectionProps> = ({
  publisherId,
  integrationData,
  updateIntegration,
  getIntegration,
  savingForm,
  setSavingForm,
  isProfilesEnabled,
  isEventsV2FeatureFlagOn
}) => {
  const resolvedVersion = useMemo(
    () => resolveEventsWebhookVersion(isEventsV2FeatureFlagOn),
    [isEventsV2FeatureFlagOn]
  );

  const analytics = useApiTestingAnalytics({
    domain: IntegrationDomain.EVENTS_CENTER,
    version: resolvedVersion
  });

  const form = useEventsWebhookForm({
    integrationData,
    updateIntegration,
    getIntegration,
    setSavingForm,
    onSaveResult: analytics.handleSaveResult
  });

  const resolveTestModalCallbackUrl = (): string => {
    if (isEventsV2FeatureFlagOn) {
      return form.initialValues.eventsV2WebhookUrl;
    }
    return form.initialValues.eventsWebhookUrl;
  };

  const callbackUrl = resolveTestModalCallbackUrl();

  const testModal = useIntegrationTestModal({
    publisherId,
    domain: IntegrationDomain.EVENTS_CENTER,
    version: resolvedVersion,
    title: 'Events Webhook Test',
    callbackUrl,
    onTestResult: analytics.trackTestExecuted
  });

  const { openModal } = testModal;

  const handleOpenModal = useCallback(() => {
    analytics.trackTestModalOpened({ url: callbackUrl });
    openModal();
  }, [analytics, callbackUrl, openModal]);

  const buildVersionConfigs = (): IVersionConfig[] => {
    const configs: IVersionConfig[] = [
      {
        version: EApiVersion.V1,
        label: EApiVersion.V1,
        value: form.values.eventsWebhookUrl,
        initialValue: form.initialValues.eventsWebhookUrl,
        onChange: (value) => form.setFieldValue('eventsWebhookUrl', value),
        error: form.touched.eventsWebhookUrl
          ? (form.errors.eventsWebhookUrl as string)
          : undefined,
        docsUrl: docsUrls.eventsCenter[IntegrationVersion.V1]
      }
    ];

    if (isEventsV2FeatureFlagOn) {
      configs.push({
        version: EApiVersion.V2,
        label: EApiVersion.V2,
        value: form.values.eventsV2WebhookUrl,
        initialValue: form.initialValues.eventsV2WebhookUrl,
        onChange: (value) => form.setFieldValue('eventsV2WebhookUrl', value),
        error: form.touched.eventsV2WebhookUrl
          ? (form.errors.eventsV2WebhookUrl as string)
          : undefined,
        docsUrl: docsUrls.eventsCenter[IntegrationVersion.V2]
      });
    }

    return configs;
  };

  const versionConfigs = buildVersionConfigs();
  const { schema, ...testModalProps } = testModal;

  return (
    <>
      <CallbackSection
        title="Events Webhook"
        description="Configure the URL where Appcharge will send real-time event notifications. Enable events in the Event Center to start receiving them via this webhook."
        versionConfigs={versionConfigs}
        onSave={form.submitForm}
        isSaveDisabled={!form.dirty || savingForm !== ESavingForm.NONE}
        isLoading={savingForm === ESavingForm.EVENTS_WEBHOOK}
        disabled={isProfilesEnabled}
        onTestClick={handleOpenModal}
        onDocsClick={analytics.trackDocsClicked}
      />
      {schema && (
        <IntegrationTestModal
          {...testModalProps}
          schema={schema}
          schemaSelectorLabel="Event Name"
        />
      )}
    </>
  );
};
